/**
 * CKEditor 4 wrapper for the React admin panel.
 *
 * Loads the same CDN build the Angular app uses
 * (`cdn.ckeditor.com/4.11.4/full-all`) so the toolbar and content filtering
 * match Angular 1:1 — full toolbar (Source, Font, Color, Alignment, Image,
 * Table, ...) and permissive content handling, so authored HTML such as styled
 * buttons renders intact. Uses the `divarea` editor type (a contenteditable
 * div, not an iframe) to mirror `ckeditor4-angular` and stay robust when the
 * editor is initialized inside a hidden tab panel or modal.
 */
import { useEffect, useRef } from 'react'
import './ckeditor4.css'

const CKEDITOR_CDN_URL = 'https://cdn.ckeditor.com/4.11.4/full-all/ckeditor.js'
const CKEDITOR_BASE_PATH = 'https://cdn.ckeditor.com/4.11.4/full-all/'

// Minimal structural typing for the CKEditor 4 global (no official types ship
// with the CDN build). Only the members this wrapper touches are declared.
interface CKEditor4Instance {
    setData: (data: string) => void
    getData: () => string
    destroy: (noUpdate?: boolean) => void
    on: (event: string, listener: (evt?: any) => void) => void
    once: (event: string, listener: (evt?: any) => void) => void
    insertHtml: (html: string) => void
    insertText: (text: string) => void
    focus: () => void
    addCommand: (name: string, definition: { exec: (editor: CKEditor4Instance, data?: any) => void }) => void
    undoManager?: { lock: () => void; unlock: () => void }
}

interface CKEditor4Static {
    replace: (element: HTMLElement, config?: Record<string, unknown>) => CKEditor4Instance
    config: Record<string, unknown>
}

declare global {
    interface Window {
        CKEDITOR?: CKEditor4Static
        CKEDITOR_BASEPATH?: string
    }
}

// The CDN build force-enables the CKEditor 4 EOL "insecure version" nag at load
// (`config.versionCheck = !isLts`, which is true for every free/non-LTS build).
// Disabling it globally — before any editor is created — reliably suppresses
// both the notification and the pseudo-element banner for every instance.
function suppressVersionNag(ckeditor: CKEditor4Static): CKEditor4Static {
    ckeditor.config.versionCheck = false
    return ckeditor
}

// Single shared loader — the CDN script is injected at most once regardless of
// how many editors mount. Mirrors ckeditor4-angular's `getEditorNamespace`.
let ckeditorLoader: Promise<CKEditor4Static> | null = null

function loadCKEditor(): Promise<CKEditor4Static> {
    if (window.CKEDITOR) return Promise.resolve(suppressVersionNag(window.CKEDITOR))
    if (ckeditorLoader) return ckeditorLoader

    ckeditorLoader = new Promise<CKEditor4Static>((resolve, reject) => {
        window.CKEDITOR_BASEPATH = CKEDITOR_BASE_PATH
        const script = document.createElement('script')
        script.src = CKEDITOR_CDN_URL
        script.async = true
        script.onload = () => {
            // Runs after the build's version-check IIFE, so the global disable
            // here wins for every editor instance created afterwards.
            if (window.CKEDITOR) resolve(suppressVersionNag(window.CKEDITOR))
            else {
                ckeditorLoader = null
                reject(new Error('CKEDITOR namespace missing after script load'))
            }
        }
        script.onerror = () => {
            ckeditorLoader = null
            reject(new Error('Failed to load CKEditor 4 from CDN'))
        }
        document.head.appendChild(script)
    })

    return ckeditorLoader
}

export type CKEditor4Props = {
    id?: string
    value: string
    onChange: (html: string) => void
    onBlur?: () => void
    onPaste?: (evt: any, instance: CKEditor4Instance) => void
    className?: string
    config?: Record<string, unknown>
}

export function CKEditor4({ id, value, onChange, onBlur, onPaste, className, config }: CKEditor4Props) {
    const containerRef = useRef<HTMLDivElement>(null)
    const instanceRef = useRef<CKEditor4Instance | null>(null)
    const readyRef = useRef(false)
    // Last HTML the editor produced/received — guards the controlled-value sync
    // below so a user's own keystrokes don't trigger a setData() (cursor jump).
    const lastDataRef = useRef(value || '')

    // Latest callbacks/props kept in refs so the create-once effect below never
    // needs to re-run (which would destroy and rebuild the editor).
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange
    const onBlurRef = useRef(onBlur)
    onBlurRef.current = onBlur
    const onPasteRef = useRef(onPaste)
    onPasteRef.current = onPaste
    const configRef = useRef(config)
    configRef.current = config
    const valueRef = useRef(value || '')
    valueRef.current = value || ''

    useEffect(() => {
        let cancelled = false

        loadCKEditor()
            .then((CKEDITOR) => {
                if (cancelled || !containerRef.current) return

                // React owns `containerRef` but never renders children into it;
                // CKEditor manages this detached target imperatively.
                const target = document.createElement('div')
                containerRef.current.appendChild(target)

                const userConfig = configRef.current ?? {}
                const userExtra = userConfig.extraPlugins
                const extraPlugins =
                    typeof userExtra === 'string' && userExtra.length ? `${userExtra},divarea` : 'divarea'

                const instance = CKEDITOR.replace(target, {
                    versionCheck: false,
                    ...userConfig,
                    extraPlugins,
                })
                instanceRef.current = instance

                instance.once('instanceReady', () => {
                    // Lock undo while seeding data so the initial load is not
                    // undoable and does not fire a change (keeps the form pristine
                    // on mount — matches the previous wrapper's intent).
                    const undo = instance.undoManager
                    undo?.lock()
                    instance.setData(valueRef.current)
                    lastDataRef.current = instance.getData()
                    undo?.unlock()
                    readyRef.current = true

                    // Emoji panel clicks blur the divarea editable; the stock
                    // insertEmoji command then uses insertHtml without a stable
                    // selection and each emoji lands in its own <p>. Re-focus and
                    // insert as text so consecutive emojis stay on one line.
                    instance.addCommand('insertEmoji', {
                        exec(editor, data) {
                            const text = data?.emojiText
                            if (!text) return
                            editor.focus()
                            editor.insertText(text)
                        },
                    })

                    instance.on('change', () => {
                        const data = instance.getData()
                        if (data === lastDataRef.current) return
                        lastDataRef.current = data
                        onChangeRef.current(data)
                    })
                    instance.on('blur', () => onBlurRef.current?.())
                    instance.on('paste', (evt: any) => onPasteRef.current?.(evt, instance))
                })
            })
            .catch(() => {
                // CDN unavailable — the editor cannot mount; container stays empty.
            })

        return () => {
            cancelled = true
            readyRef.current = false
            const instance = instanceRef.current
            instanceRef.current = null
            if (instance) {
                try {
                    instance.destroy()
                } catch {
                    // May already be torn down during a fast unmount/remount.
                }
            }
        }
    }, [])

    // Controlled-value sync: push external value changes (form reset, async
    // record load) into the editor. Skips the echo of the user's own edits.
    useEffect(() => {
        const instance = instanceRef.current
        if (!instance || !readyRef.current) return
        const next = value || ''
        if (next === lastDataRef.current) return
        if (instance.getData() === next) return
        instance.setData(next)
        lastDataRef.current = instance.getData()
    }, [value])

    return <div className={className} data-editor-id={id} ref={containerRef} />
}
